// ============================================================================
// ActivitySection — the delivered work: total sessions + total hours, and the
// per-client breakdowns (busiest clients by sessions, hours per client). Uses
// animated RankBars for the busiest clients and a Pulse Table for the full
// per-client detail. Hours rows render only when there's any duration logged.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Table } from "../../../components";
import type { Column } from "../../../components";
import { ReportSection } from "./ReportSection";
import { RankBars } from "../charts/RankBars";
import type { TypedBusinessReport } from "../types";
import { fmtNum, fmtHours } from "../format";

interface ClientRow {
  id: number;
  name: string;
  sessions: number;
  hours: number | null;
}

export function ActivitySection({
  report,
  index,
}: {
  report: TypedBusinessReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("businessReport");
  const locale = i18n.language;
  const { sessions, hours, income } = report;
  // No-shows are excluded from the delivered counts above; surface that so the
  // numbers can't be mistaken for "every session that was on the calendar".
  const noShowCount = income.no_show_count ?? 0;

  // Merge sessions + hours per client into one ranked table (busiest first;
  // the backend already sorts sessions desc, then name).
  const hoursById = new Map<number, number>(
    hours.per_client.map((h) => [h.client_id, h.hours]),
  );
  const rows: ClientRow[] = sessions.per_client.map((s) => ({
    id: s.client_id,
    name: s.client_name ?? t("common.unnamedClient"),
    sessions: s.sessions,
    hours: hoursById.get(s.client_id) ?? null,
  }));

  // Busiest-by-sessions bars (top 6 keeps a phone readable).
  const busiest = sessions.per_client.slice(0, 6).map((s) => ({
    id: s.client_id,
    label: s.client_name ?? t("common.unnamedClient"),
    value: s.sessions,
  }));

  const showHoursColumn = hours.ok && rows.some((r) => r.hours !== null);

  const columns: Column<ClientRow>[] = [
    {
      key: "name",
      header: t("activity.client"),
      cell: (r) => (
        <span className="font-display font-bold text-body">{r.name}</span>
      ),
    },
    {
      key: "sessions",
      header: t("activity.sessions"),
      align: "right",
      cell: (r) => (
        <span className="nums font-bold">{fmtNum(r.sessions, 0, locale)}</span>
      ),
    },
  ];
  if (showHoursColumn) {
    columns.push({
      key: "hours",
      header: t("activity.hours"),
      align: "right",
      cell: (r) =>
        r.hours !== null ? (
          <span className="nums font-bold">
            {fmtHours(r.hours, locale)}
            {t("units.hoursSuffix")}
          </span>
        ) : (
          <span className="text-body-faint">—</span>
        ),
    });
  }

  return (
    <ReportSection
      id="business-activity"
      index={index}
      kicker={t("kicker")}
      title={t("activity.title")}
      subtitle={t("activity.subtitle")}
      hasData={sessions.ok}
    >
      <div className="space-y-8">
        {/* Headline totals */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg bg-coral-soft p-4 text-center">
            <p className="nums font-display text-3xl font-extrabold tracking-tightest text-volt-600">
              {fmtNum(sessions.total, 0, locale)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("activity.sessionsDelivered")}
            </p>
          </div>
          <div className="rounded-lg bg-ocean-soft p-4 text-center">
            <p className="nums font-display text-3xl font-extrabold tracking-tightest text-ocean-600">
              {hours.ok ? fmtHours(hours.total, locale) : "—"}
              {hours.ok && (
                <span className="text-lg">{t("units.hoursSuffix")}</span>
              )}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("activity.hoursTotal")}
            </p>
          </div>
        </div>

        {/* No-shows are excluded from the delivered counts (and from income). */}
        {noShowCount > 0 && (
          <p className="-mt-4 flex items-center gap-1.5 text-xs font-medium text-body-soft">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full bg-amber"
            />
            {t("activity.noShowNote", { count: noShowCount })}
          </p>
        )}

        {/* Busiest clients (animated bars) */}
        {busiest.length > 0 && (
          <div>
            <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
              {t("activity.busiest")}
            </h3>
            <RankBars
              data={busiest}
              accent="coral"
              formatValue={(v) =>
                `${fmtNum(v, 0, locale)} ${t("activity.sessionsShort")}`
              }
            />
          </div>
        )}

        {/* Full per-client breakdown table */}
        {rows.length > 0 && (
          <div>
            <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
              {t("activity.breakdown")}
            </h3>
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              caption={t("activity.breakdown")}
            />
          </div>
        )}
      </div>
    </ReportSection>
  );
}
